import React, { useState, useEffect } from 'react';
import ApiBaseUrl from '../constants/apiUrl';
import { useNavigate } from 'react-router-dom';
import Pagination from '../compontents/Pagination';

const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 4000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';

    return (
        <div className={`fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-3`}>
            <span className="font-medium">{message}</span>
            <button onClick={onClose} className="ml-2 hover:opacity-80 text-xl leading-none">&times;</button>
        </div>
    );
};

const InsuranceApplicationsList = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const [filterPolicyType, setFilterPolicyType] = useState('All');
    const [filterCreator, setFilterCreator] = useState('all'); // all, partner, agent
    const [currentPage, setCurrentPage] = useState(1);
    const [toast, setToast] = useState(null);
    const itemsPerPage = 10;
    const navigate = useNavigate();

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    useEffect(() => {
        fetchApplications();
    }, []);

    const fetchApplications = async () => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth'));
            const res = await fetch(`${ApiBaseUrl}/getAllInsuranceApplications`, {
                headers: {
                    'Authorization': `Bearer ${authData.token}`,
                }
            });
            const data = await res.json();
            if (data.success) {
                // Fetch plan details to get creator information
                const applicationsWithCreatorInfo = await Promise.all(
                    (data.data || []).map(async (app) => {
                        try {
                            if (app.planId) {
                                const planRes = await fetch(`${ApiBaseUrl}/getInsurancePlanAdmin/${app.planId}`, {
                                    headers: {
                                        'Authorization': `Bearer ${authData.token}`,
                                    }
                                });
                                const planData = await planRes.json();
                                if (planData.success && planData.data) {
                                    // Check if createdBy is an agent or partner by checking user type
                                    let creatorType = 'partner'; // default
                                    if (planData.data.createdBy) {
                                        try {
                                            const userRes = await fetch(`${ApiBaseUrl}/getUserById?userId=${planData.data.createdBy}`, {
                                                headers: {
                                                    'Authorization': `Bearer ${authData.token}`,
                                                }
                                            });
                                            const userData = await userRes.json();
                                            if (userData.success && userData.data) {
                                                creatorType = userData.data.UserType === 'agent' ? 'agent' : 'partner';
                                            }
                                        } catch (err) {
                                            console.error('Error fetching creator user info:', err);
                                        }
                                    }
                                    
                                    return {
                                        ...app,
                                        planCreatedBy: planData.data.createdBy,
                                        planCreatedByType: creatorType,
                                    };
                                }
                            }
                            return app;
                        } catch (err) {
                            console.error('Error fetching plan details:', err);
                            return app;
                        }
                    })
                );
                setApplications(applicationsWithCreatorInfo);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError("Failed to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (applicationId, newStatus) => {
        const adminNote = window.prompt(`Update status to ${newStatus}. Add a note (optional):`) || '';
        
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth'));
            const res = await fetch(`${ApiBaseUrl}/updateInsuranceApplicationStatus/${applicationId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${authData.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    status: newStatus,
                    adminNote: adminNote
                })
            });
            
            const data = await res.json();
            
            if (res.ok && data.success) {
                fetchApplications();
                showToast(`Application status updated to ${newStatus}`);
            } else {
                showToast(data.message || 'Failed to update status', 'error');
            }
        } catch (err) {
            showToast('Network error. Please try again.', 'error');
        }
    };

    const filtered = applications.filter(app => {
        const matchesSearch = 
            app.applicationId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.applicantInfo?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.planName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'All' || app.status === filterStatus.toLowerCase();
        const matchesPolicyType = filterPolicyType === 'All' || app.policyType === filterPolicyType;
        const matchesCreator = filterCreator === 'all' || 
            (filterCreator === 'partner' && app.planCreatedByType !== 'agent') ||
            (filterCreator === 'agent' && app.planCreatedByType === 'agent');
        return matchesSearch && matchesStatus && matchesPolicyType && matchesCreator;
    });

    const paginatedApplications = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const getStatusBadge = (status) => {
        const badges = {
            new: 'bg-blue-100 text-blue-800',
            under_review: 'bg-yellow-100 text-yellow-800',
            docs_required: 'bg-orange-100 text-orange-800',
            submitted: 'bg-purple-100 text-purple-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
        };
        return badges[status] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
            
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Insurance Applications</h1>
                <p className="text-gray-600">Manage all insurance applications from users</p>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <input
                        type="text"
                        placeholder="Search applications..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                        <option value="All">All Status</option>
                        <option value="new">New</option>
                        <option value="under_review">Under Review</option>
                        <option value="docs_required">Docs Required</option>
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                    </select>
                    <select
                        value={filterPolicyType}
                        onChange={(e) => setFilterPolicyType(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                        <option value="All">All Policy Types</option>
                        <option value="Life">Life</option>
                        <option value="Health">Health</option>
                        <option value="Motor">Motor</option>
                        <option value="Travel">Travel</option>
                        <option value="Property">Property</option>
                        <option value="Takaful">Takaful</option>
                    </select>
                    <select
                        value={filterCreator}
                        onChange={(e) => setFilterCreator(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                        <option value="all">All Creators</option>
                        <option value="partner">Partner Created</option>
                        <option value="agent">Agent Created</option>
                    </select>
                </div>
            </div>

            {/* Applications Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Application ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applicant</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Policy Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedApplications.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                                        No applications found
                                    </td>
                                </tr>
                            ) : (
                                paginatedApplications.map((app) => (
                                    <tr key={app._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{app.applicationId}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{app.applicantInfo?.fullName || 'N/A'}</div>
                                            <div className="text-sm text-gray-500">{app.applicantInfo?.cnic || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{app.planName || 'N/A'}</div>
                                            <div className="text-sm text-gray-500">{app.registeredCompanyName || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                {app.policyType || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                app.planCreatedByType === 'agent' 
                                                    ? 'bg-purple-100 text-purple-800' 
                                                    : 'bg-green-100 text-green-800'
                                            }`}>
                                                {app.planCreatedByType === 'agent' ? 'Agent' : 'Partner'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(app.status)}`}>
                                                {app.status || 'new'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(app.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/insurance/application/${app._id}`)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                >
                                                    View
                                                </button>
                                                <select
                                                    value={app.status}
                                                    onChange={(e) => handleStatusUpdate(app._id, e.target.value)}
                                                    className="text-sm border border-gray-300 rounded px-2 py-1"
                                                >
                                                    <option value="new">New</option>
                                                    <option value="under_review">Under Review</option>
                                                    <option value="docs_required">Docs Required</option>
                                                    <option value="submitted">Submitted</option>
                                                    <option value="approved">Approved</option>
                                                    <option value="rejected">Rejected</option>
                                                </select>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {filtered.length > itemsPerPage && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(filtered.length / itemsPerPage)}
                    onPageChange={setCurrentPage}
                />
            )}
        </div>
    );
};

export default InsuranceApplicationsList;
